import React, { useState, useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { CreateExerciseData } from '../types/dataModel';
import { muscleGroups, equipmentTypes } from '../data/exercises';

interface CreateExerciseFormProps {
  onSubmit: (exerciseData: CreateExerciseData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  /** When set, form is in edit mode: prefilled and submit saves changes. */
  initialData?: Partial<CreateExerciseData> & { isPublic?: boolean };
  title?: string;
  submitLabel?: string;
  loadingLabel?: string;
  /** When true, show a note that changes apply only to this trainer. */
  isLibraryOverride?: boolean;
  /**
   * Fixed overlay z-index class. Use z-[125] (or higher) when stacking above exercise detail / workout overlays (z-115).
   * @default z-[110]
   */
  overlayZClassName?: string;
  /**
   * When true, no full-screen dimmer — parent (e.g. workout exercise scrim) stays mounted to avoid backdrop flicker.
   * Outer wrapper is pointer-events-none except the card (pointer-events-auto).
   */
  embedInExerciseOverlay?: boolean;
  /**
   * When true with embedInExerciseOverlay, wrapper is `absolute` inside the workout exercise stack (portaled).
   * Avoids a second `position:fixed` stacking context on Safari.
   */
  embeddedInWorkoutExerciseStack?: boolean;
}

const CreateExerciseForm: React.FC<CreateExerciseFormProps> = ({ 
  onSubmit, 
  onCancel, 
  isLoading = false,
  initialData,
  title = 'Create a New Exercise',
  submitLabel = 'Create Exercise',
  loadingLabel = 'Creating Exercise...',
  isLibraryOverride = false,
  overlayZClassName = 'z-[110]',
  embedInExerciseOverlay = false,
  embeddedInWorkoutExerciseStack = false,
}) => {
  const [formData, setFormData] = useState<CreateExerciseData>(() => ({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    muscleGroup: initialData?.muscleGroup ?? '',
    equipment: initialData?.equipment ?? '',
    image: initialData?.image ?? '',
    video: initialData?.video ?? '',
    isPublic: initialData?.isPublic ?? true
  }));

  // Only sync initialData on mount (when modal opens). Parent passes a new object every render;
  // re-syncing on [initialData] would overwrite user edits when they click Save and parent re-renders.
  const initialDataAppliedRef = React.useRef(false);
  useEffect(() => {
    if (initialData && !initialDataAppliedRef.current) {
      initialDataAppliedRef.current = true;
      setFormData({
        name: initialData.name ?? '',
        description: initialData.description ?? '',
        muscleGroup: initialData.muscleGroup ?? '',
        equipment: initialData.equipment ?? '',
        image: initialData.image ?? '',
        video: initialData.video ?? '',
        isPublic: initialData.isPublic ?? true
      });
    }
    if (!initialData) initialDataAppliedRef.current = false;
  }, [initialData]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Exercise name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Exercise form submit error:', error);
      const message = error instanceof Error ? error.message : (error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message) : 'Failed to save. Please try again.');
      alert(message);
    }
  };

  const inputClass = "w-full px-3 py-2.5 text-sm border border-theme-border dark:border-white/10 rounded-md text-theme-text dark:text-theme-off-white bg-theme-bg dark:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-accent-violet dark:focus:ring-accent-lime focus:border-accent-violet dark:focus:border-accent-lime placeholder:text-theme-text/50 dark:placeholder:text-theme-off-white/50";
  const labelClass = "block text-sm font-medium text-theme-text dark:text-theme-off-white mb-1.5";

  const card = (
      <div className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-theme-border dark:border-white/10 shadow-xl rounded-md bg-theme-bg dark:bg-theme-surface pointer-events-auto">
        {/* Header */}
        <div className="sticky top-0 bg-theme-bg dark:bg-theme-surface border-b border-theme-border dark:border-white/10 p-6 rounded-t-md">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-theme-text dark:text-theme-off-white">{title}</h2>
            <button type="button" onClick={onCancel} className="p-1 text-theme-text/60 hover:text-theme-text dark:hover:text-theme-off-white rounded-full transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {isLibraryOverride && (
            <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
              Your changes are saved only for you. Other trainers will still see the original exercise.
            </p>
          )}
          {/* Exercise Name */}
          <div>
            <label className={labelClass}>
              Exercise Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className={inputClass}
              placeholder="e.g., Custom Push-up Variation"
            />
            {errors.name && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className={inputClass}
              placeholder="Describe the exercise and steps"
            />
          </div>

          {/* Muscle Group */}
          <div>
            <label className={labelClass}>
              Muscle Group
            </label>
            <select
              value={formData.muscleGroup || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, muscleGroup: e.target.value }))}
              className={inputClass}
            >
              <option value="">Select muscle group</option>
              {muscleGroups.map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>

          {/* Equipment */}
          <div>
            <label className={labelClass}>
              Equipment
            </label>
            <select
              value={formData.equipment || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, equipment: e.target.value }))}
              className={inputClass}
            >
              <option value="">Select equipment</option>
              {equipmentTypes.map((equip) => (
                <option key={equip} value={equip}>{equip}</option>
              ))}
            </select>
          </div>

          {/* Image URL */}
          <div>
            <label className={labelClass}>
              Image URL
            </label>
            <input
              type="url"
              value={formData.image || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
              className={inputClass}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          {/* Video URL */}
          <div>
            <label className={labelClass}>
              Video URL
            </label>
            <input
              type="url"
              value={formData.video || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, video: e.target.value }))}
              className={inputClass}
              placeholder="https://example.com/video.mp4"
            />
          </div>

          {/* Public/Private Toggle */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="rounded border-theme-border dark:border-white/10 text-accent-violet dark:text-accent-lime focus:ring-accent-violet dark:focus:ring-accent-lime"
              />
              <span className="text-sm font-medium text-theme-text dark:text-theme-off-white">
                Make this exercise public (visible to all users if approved)
              </span>
            </label>
          </div>

          {/* Submit only - no Cancel */}
          <div className="flex justify-end pt-4 border-t border-theme-border dark:border-white/10">
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2.5 text-sm font-medium text-white dark:text-theme-bg-dark bg-accent-violet dark:bg-accent-lime border border-transparent rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-violet dark:focus:ring-accent-lime disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent-violet/25 dark:shadow-accent-lime/25 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white dark:border-theme-bg-dark border-t-transparent" />
                  {loadingLabel}
                </>
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </form>
      </div>
  );

  if (embedInExerciseOverlay) {
    const stack = embeddedInWorkoutExerciseStack
      ? 'absolute inset-0 z-[20] flex items-center justify-center p-4 overflow-y-auto pointer-events-none [isolation:isolate] [transform:translate3d(0,0,0)] transition-none motion-reduce:transition-none'
      : `fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none [isolation:isolate] [transform:translate3d(0,0,0)] bg-theme-text/[0.015] dark:bg-black/[0.015] transition-none ${overlayZClassName}`;
    const body =
      embeddedInWorkoutExerciseStack ? (
        /* No extra full-bleed alpha layer — z-110 workout scrim + panel already stack; extra veil caused Safari compositor flashes. */
        <div className="relative z-[1] w-full flex justify-center">{card}</div>
      ) : (
        card
      );
    return <div className={stack}>{body}</div>;
  }

  return (
    <div
      className={`fixed inset-0 bg-theme-text/50 dark:bg-black/50 overflow-y-auto h-full w-full backdrop-blur-sm flex items-center justify-center p-4 ${overlayZClassName}`}
    >
      {card}
    </div>
  );
};

export default CreateExerciseForm;
